import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useLangPath } from '../../hooks/useLangPath';

// ─── All WIZ Projects ─────────────────────────────────────────────────────────
const ALL_PROJECTS = [
  {
    id: 1, category: 'HealthTech · Mobile',
    title: 'Medical Platform',
    description: 'Comprehensive healthcare app for managing appointments, publishing medical content, and tracking patient follow-ups.',
    technologies: ['Flutter', 'Laravel', 'Firebase', 'REST API'],
    deliverables: ['Mobile App (iOS & Android)', 'Admin Dashboard', 'Patient Portal'],
    link: '',
    images: [
      '/assets/images/projects/Medical-Platform/Screenshot 2024-04-25 at 2.11.38.png',
      '/assets/images/projects/Medical-Platform/Screenshot 2024-04-25 at 2.29.48.png',
    ],
    highlight: 'Improved clinic efficiency & patient engagement',
    color: '#45d0bd',
  },
  {
    id: 2, category: 'Restaurant Tech · POS',
    title: 'KIOSK Self-Ordering Screen',
    description: 'Interactive touch-screen kiosk for restaurants — browse menu, customize meals, and pay via integrated e-payment gateways.',
    technologies: ['Android', 'REST API', 'POS Integration', 'UI/UX'],
    deliverables: ['Kiosk Application', 'UI System', 'Payment Integration'],
    link: 'pos.wiz-tech.co',
    images: [
      '/assets/images/projects/Touch-scree-ordering-app/Screenshot 2024-04-25 at 0.41.00.png',
      '/assets/images/projects/Touch-scree-ordering-app/Screenshot 2024-04-25 at 0.41.07.png',
    ],
    highlight: 'Built from scratch — design, animation & logic',
    color: '#8258c8',
  },
  {
    id: 3, category: 'Islamic App · Mobile',
    title: 'Islamic App',
    description: 'Full-featured Islamic mobile app delivering prayer times, Quran recitation, Duaa database, and spiritual reminders.',
    technologies: ['Mobile', 'Firebase', 'Cloud', 'REST API'],
    deliverables: ['Mobile App (iOS & Android)', 'Content System', 'Push Notifications'],
    link: '',
    images: [
      '/assets/images/projects/Islamic-App/Screenshot 2024-04-25 at 0.25.29.png',
      '/assets/images/projects/Islamic-App/Screenshot 2024-04-25 at 0.25.50.png',
    ],
    highlight: 'Rich spiritual companion with offline support',
    color: '#2c84c8',
  },
  {
    id: 4, category: 'HealthTech · Verification',
    title: 'Iraq NovoCheck',
    description: 'Pharmaceutical verification app for the Iraqi market — scan barcodes or enter batch numbers to validate Novo Nordisk medicines.',
    technologies: ['Android', 'iOS', 'Camera API', 'REST API'],
    deliverables: ['Mobile App', 'Barcode Scanner', 'Verification Engine'],
    link: '',
    images: [
      '/assets/images/projects/NovoCheck/1.png',
      '/assets/images/projects/NovoCheck/2.png',
    ],
    highlight: 'Combats counterfeit medicines in Iraq',
    color: '#45d0bd',
  },
  {
    id: 5, category: 'AI · Computer Vision',
    title: 'FaceBot AI',
    description: 'Real-time face recognition app powered by advanced computer vision models — optimized for fast mobile inference and high accuracy.',
    technologies: ['AI', 'Computer Vision', 'Mobile', 'TensorFlow'],
    deliverables: ['Mobile App', 'AI Recognition Engine', 'API Backend'],
    link: '',
    images: [
      '/assets/images/projects/FaceBot-App/Android Large - 13.png',
    ],
    highlight: 'High-accuracy real-time identification',
    color: '#8258c8',
  },
  {
    id: 6, category: 'HealthTech · Clinic',
    title: 'Hakeem Oyoon',
    description: 'Healthcare mobile app for eye clinics — book appointments, receive health content, and stay connected with specialists.',
    technologies: ['Mobile', 'Laravel', 'Firebase'],
    deliverables: ['Healthcare App', 'Appointment System', 'Content Manager'],
    link: 'https://play.google.com/store/apps/details?id=com.esite_iq.hakeem',
    images: [
      '/assets/images/projects/Hakeem-oyon-app/Screenshot 2024-04-25 at 0.16.41.png',
      '/assets/images/projects/Hakeem-oyon-app/Screenshot 2024-04-25 at 0.16.48.png',
    ],
    highlight: 'Live on Google Play Store',
    color: '#2c84c8',
  },
  {
    id: 7, category: 'E-commerce · AI',
    title: 'AI E-commerce Platform',
    description: 'Modern AI-powered e-commerce platform with intelligent product recommendations, dynamic search, and a robust multi-vendor backend.',
    technologies: ['Web', 'AI', 'Laravel', 'Vue.js'],
    deliverables: ['E-commerce Website', 'Admin Dashboard', 'AI Recommendation Engine'],
    link: '',
    images: [
      '/assets/images/projects/AI-Power-ecommerce-platform/Screenshot 2024-04-28 at 22.41.10.png',
    ],
    highlight: 'Personalized shopping at scale',
    color: '#45d0bd',
  },
  {
    id: 8, category: 'Social · Sports',
    title: 'Sport Challenge App',
    description: 'Social sports platform for creating teams, joining challenges, tracking performance stats, and building a vibrant athlete community.',
    technologies: ['Mobile', 'Firebase', 'REST API'],
    deliverables: ['Mobile App', 'Social Feed', 'Leaderboards'],
    link: '',
    images: [
      '/assets/images/projects/Sport-App/Screenshot 2024-04-25 at 3.02.12.png',
    ],
    highlight: 'Gamifying fitness & competition',
    color: '#8258c8',
  },
  {
    id: 9, category: 'Islamic App · Web',
    title: 'Duaa Database',
    description: 'Modern web platform serving as a comprehensive database of Islamic supplications with advanced search, categorization, and smooth UX.',
    technologies: ['MySQL', 'Web Stack', 'Laravel'],
    deliverables: ['Website', 'Search Engine', 'Content Management System'],
    link: 'duaadatabase.com',
    images: [
      '/assets/images/projects/Duaa-App/Screenshot 2024-03-21 at 2.07.28.png',
    ],
    highlight: 'Thousands of searchable supplications',
    color: '#2c84c8',
  },
  {
    id: 10, category: 'Restaurant Tech · Smart',
    title: 'DineOS — Smart Restaurant System',
    description: 'Complete restaurant management solution covering smart ordering, kitchen display, AI-driven cost optimization, and customer experience.',
    technologies: ['Android', 'Web', 'REST API', 'POS', 'AI'],
    deliverables: ['Smart Ordering App', 'Kitchen Display', 'Manager Dashboard', 'AI Analytics'],
    link: 'pos.wiz-tech.co',
    images: [
      '/assets/images/projects/DineOS/Screenshot 2024-04-25 at 2.03.29.png',
      '/assets/images/projects/DineOS/Screenshot 2024-04-25 at 2.03.32.png',
    ],
    highlight: 'AI cuts operational cost by 30%',
    color: '#45d0bd',
  },
  {
    id: 11, category: 'AI · Robotics · HealthTech',
    title: 'Autism Therapy Robot',
    description: 'Intelligent therapy robot for autism spectrum disorder treatment — adaptive programs, structured interactions, and secure data tracking.',
    technologies: ['Embedded Systems', 'AI', 'Database'],
    deliverables: ['Therapy Robot System', 'Backend Integration', 'Data Dashboard'],
    link: 'qlickhealth.ai',
    images: [],
    highlight: 'Pioneering AI-assisted autism therapy',
    color: '#8258c8',
  },
  {
    id: 12, category: 'SaaS · Mosque Platform',
    title: 'Masjid Platform',
    description: 'Full-scale digital ecosystem for mosque management — web admin panel, mobile backend, prayer schedules, events, donations, and community tools.',
    technologies: ['Laravel', 'Vue.js', 'PHP', 'Android', 'iOS'],
    deliverables: ['Admin Dashboard', 'REST API', 'Mobile Backend'],
    link: 'https://masjid.hopetechapps.com',
    images: [],
    highlight: 'Centralized mosque management platform',
    color: '#2c84c8',
  },
  {
    id: 13, category: 'AI · EdTech · Islamic Tech',
    title: 'Oman Tartell — AI Quran',
    description: 'AI-powered Quran memorization assistant that listens to real-time recitation, detects errors with high accuracy, and gives instant corrective feedback.',
    technologies: ['AI', 'Speech Recognition', 'Cloud', 'Mobile'],
    deliverables: ['Mobile App', 'AI Engine', 'Progress Tracker'],
    link: '',
    images: [
      '/assets/images/projects/Islamic-App/Screenshot 2024-04-25 at 0.25.29.png',
    ],
    highlight: 'Real-time Quran error correction',
    color: '#45d0bd',
  },
  {
    id: 14, category: 'AI · IoT · Utility',
    title: 'Electrical Reading Collector',
    description: 'Smart system for automated collection of electrical meter readings using AI and OCR — real-time integration, billing analytics, and a comprehensive dashboard.',
    technologies: ['Flutter', 'AI', 'OCR', 'REST API', 'Real-time'],
    deliverables: ['Mobile App', 'Web Dashboard', 'AI Reading Analysis'],
    link: 'https://collector.wiz-tech.co',
    images: [],
    highlight: 'Automated meter reading at scale',
    color: '#8258c8',
  },
  {
    id: 15, category: 'SDK · Computer Vision',
    title: 'MRZ Scanner Android SDK',
    description: 'High-precision Android SDK for reading and validating passport MRZ data using advanced OCR and image processing — built for airports, hotels, and fintech.',
    technologies: ['Android', 'Kotlin', 'Tesseract OCR', 'OpenCV'],
    deliverables: ['Android SDK', 'MRZ Parser', 'Verification Engine'],
    link: '',
    images: [],
    highlight: 'Airport & fintech-grade passport scanning',
    color: '#2c84c8',
  },
  {
    id: 16, category: 'Event Management · Booking',
    title: 'FIFA Arab Qatar System',
    description: 'Specialized event management and booking system for accommodation and VIP ticketing during major events like the FIFA Arab Cup.',
    technologies: ['Web', 'Mobile', 'Booking Systems'],
    deliverables: ['Booking System', 'VIP Dashboard', 'Event Management'],
    link: 'seating.wiz-tech.co',
    images: [],
    highlight: 'Used at FIFA Arab Cup events',
    color: '#45d0bd',
  },
  {
    id: 17, category: 'FinTech · Mobile',
    title: 'Safqa App',
    description: 'Smart invoice and account management mobile app — create, send, and track invoices with full payment tracking and financial record keeping.',
    technologies: ['Flutter', 'Laravel', 'Firebase', 'REST API'],
    deliverables: ['Mobile App (Android & iOS)', 'Invoice System', 'Account Dashboard'],
    link: 'https://safqa.wiz-tech.co',
    images: [],
    highlight: 'Simplifying invoicing for businesses',
    color: '#8258c8',
  },
  {
    id: 18, category: 'FinTech · AI',
    title: 'Smart Ledger',
    description: 'AI-powered financial management system for analyzing transactions, managing accounts, and generating intelligent financial insights.',
    technologies: ['AI', 'Web', 'Financial APIs'],
    deliverables: ['Finance Dashboard', 'AI Analytics', 'Account Management'],
    link: 'demo.wiz-tech.co',
    images: [],
    highlight: 'AI-driven financial intelligence',
    color: '#2c84c8',
  },
  {
    id: 19, category: 'Travel Platform · Web',
    title: 'BlueYamama.com',
    description: 'Travel aggregation platform that compares offers from multiple booking engines — find the best flights, hotels, and trips in one place.',
    technologies: ['Web', 'Aggregation API', 'Search'],
    deliverables: ['Aggregator Website', 'Comparison Engine', 'Search System'],
    link: 'BlueYamama.com',
    images: [],
    highlight: 'Best travel deals aggregated',
    color: '#45d0bd',
  },
  {
    id: 20, category: 'AI · Safety · Industrial',
    title: 'Bulldozer Human Safety App',
    description: 'Real-time AI safety system detecting human presence around heavy machinery and instantly alerting operators — built for harsh industrial environments.',
    technologies: ['AI', 'Android', 'Edge AI', 'Computer Vision'],
    deliverables: ['Safety App', 'Detection Model', 'Alert System'],
    link: '',
    images: [],
    highlight: 'Preventing construction site accidents',
    color: '#8258c8',
  },
  {
    id: 21, category: 'E-commerce · Furniture',
    title: 'BM Meubelen',
    description: 'Elegant e-commerce website for handcrafted wood furniture — category browsing, wishlist, shopping cart, and a custom design ordering system.',
    technologies: ['Web', 'E-commerce', 'Responsive Design'],
    deliverables: ['Full E-commerce Website', 'Product Catalog', 'Custom Order System'],
    link: 'https://bmmeubelen.com',
    images: [],
    highlight: 'Premium handcrafted furniture online',
    color: '#2c84c8',
  },
  {
    id: 22, category: 'Architecture · Web',
    title: 'Utopia4Design',
    description: 'Official website for Utopia Engineering — architecture, urban planning, and interior design consultancy based in Gaza, showcasing portfolio and services.',
    technologies: ['Web', 'Responsive Framework', 'Custom Design'],
    deliverables: ['Company Website', 'Project Portfolio', 'Contact System'],
    link: 'https://utopia4design.com',
    images: [],
    highlight: 'Established architecture firm since 2004',
    color: '#45d0bd',
  },
  {
    id: 23, category: 'Cultural · Publishing',
    title: 'East Research Center',
    description: 'Comprehensive cultural and scientific platform for an Arab research center — e-library, online bookstore, blog, opinion polls, and educational activities.',
    technologies: ['Web', 'CMS', 'E-commerce', 'Multi-language'],
    deliverables: ['Full Website', 'Electronic Library', 'Online Book Store', 'Blog System'],
    link: 'https://east-cr.com',
    images: [],
    highlight: 'Premier Arab cultural publishing hub',
    color: '#8258c8',
  },
];

const ALL_PROJECTS_AR = [
  { id: 1, category: 'تقنية الصحة · جوال', title: 'المنصة الطبية', description: 'تطبيق رعاية صحية شامل لإدارة المواعيد ونشر المحتوى الطبي وتتبع متابعات المرضى.', technologies: ['Flutter', 'Laravel', 'Firebase', 'REST API'], deliverables: ['تطبيق جوال (iOS وAndroid)', 'لوحة المشرف', 'بوابة المريض'], link: '', images: ['/assets/images/projects/Medical-Platform/Screenshot 2024-04-25 at 2.11.38.png', '/assets/images/projects/Medical-Platform/Screenshot 2024-04-25 at 2.29.48.png'], highlight: 'تحسين كفاءة العيادة وتفاعل المرضى', color: '#45d0bd' },
  { id: 2, category: 'تقنية المطاعم · POS', title: 'شاشة الطلب الذاتي KIOSK', description: 'كشك بشاشة لمس تفاعلية للمطاعم — تصفح القائمة وتخصيص الوجبات والدفع عبر بوابات الدفع الإلكتروني.', technologies: ['Android', 'REST API', 'POS Integration', 'UI/UX'], deliverables: ['تطبيق الكشك', 'نظام واجهة المستخدم', 'تكامل الدفع'], link: 'pos.wiz-tech.co', images: ['/assets/images/projects/Touch-scree-ordering-app/Screenshot 2024-04-25 at 0.41.00.png', '/assets/images/projects/Touch-scree-ordering-app/Screenshot 2024-04-25 at 0.41.07.png'], highlight: 'بُني من الصفر — التصميم والحركة والمنطق', color: '#8258c8' },
  { id: 3, category: 'تطبيق إسلامي · جوال', title: 'التطبيق الإسلامي', description: 'تطبيق جوال إسلامي متكامل يقدم أوقات الصلاة وتلاوة القرآن وقاعدة بيانات الأدعية والتذكيرات الروحية.', technologies: ['Mobile', 'Firebase', 'Cloud', 'REST API'], deliverables: ['تطبيق جوال (iOS وAndroid)', 'نظام المحتوى', 'الإشعارات الفورية'], link: '', images: ['/assets/images/projects/Islamic-App/Screenshot 2024-04-25 at 0.25.29.png', '/assets/images/projects/Islamic-App/Screenshot 2024-04-25 at 0.25.50.png'], highlight: 'رفيق روحي غني يعمل دون اتصال', color: '#2c84c8' },
  { id: 4, category: 'تقنية الصحة · التحقق', title: 'Iraq NovoCheck', description: 'تطبيق التحقق من الأدوية للسوق العراقي — مسح الباركود أو إدخال أرقام الدفعات للتحقق من أدوية Novo Nordisk.', technologies: ['Android', 'iOS', 'Camera API', 'REST API'], deliverables: ['تطبيق جوال', 'ماسح الباركود', 'محرك التحقق'], link: '', images: ['/assets/images/projects/NovoCheck/1.png', '/assets/images/projects/NovoCheck/2.png'], highlight: 'يحارب الأدوية المزيفة في العراق', color: '#45d0bd' },
  { id: 5, category: 'ذكاء اصطناعي · رؤية حاسوبية', title: 'FaceBot AI', description: 'تطبيق التعرف على الوجه في الوقت الفعلي مدعوم بنماذج رؤية حاسوبية متقدمة — محسّن للاستدلال السريع على الجوال وعالي الدقة.', technologies: ['AI', 'Computer Vision', 'Mobile', 'TensorFlow'], deliverables: ['تطبيق جوال', 'محرك التعرف بالذكاء الاصطناعي', 'خلفية API'], link: '', images: ['/assets/images/projects/FaceBot-App/Android Large - 13.png'], highlight: 'تعريف فوري بدقة عالية', color: '#8258c8' },
  { id: 6, category: 'تقنية الصحة · عيادة', title: 'حكيم عيون', description: 'تطبيق رعاية صحية لعيادات العيون — حجز المواعيد واستقبال المحتوى الصحي والتواصل مع المتخصصين.', technologies: ['Mobile', 'Laravel', 'Firebase'], deliverables: ['تطبيق الرعاية الصحية', 'نظام المواعيد', 'مدير المحتوى'], link: 'https://play.google.com/store/apps/details?id=com.esite_iq.hakeem', images: ['/assets/images/projects/Hakeem-oyon-app/Screenshot 2024-04-25 at 0.16.41.png', '/assets/images/projects/Hakeem-oyon-app/Screenshot 2024-04-25 at 0.16.48.png'], highlight: 'متاح على متجر Google Play', color: '#2c84c8' },
  { id: 7, category: 'تجارة إلكترونية · ذكاء اصطناعي', title: 'منصة التجارة الإلكترونية بالذكاء الاصطناعي', description: 'منصة تجارة إلكترونية حديثة مدعومة بالذكاء الاصطناعي مع توصيات ذكية للمنتجات وبحث ديناميكي وخلفية متعددة البائعين.', technologies: ['Web', 'AI', 'Laravel', 'Vue.js'], deliverables: ['موقع التجارة الإلكترونية', 'لوحة المشرف', 'محرك التوصية بالذكاء الاصطناعي'], link: '', images: ['/assets/images/projects/AI-Power-ecommerce-platform/Screenshot 2024-04-28 at 22.41.10.png'], highlight: 'تسوق مخصص على نطاق واسع', color: '#45d0bd' },
  { id: 8, category: 'اجتماعي · رياضة', title: 'تطبيق تحديات رياضية', description: 'منصة رياضية اجتماعية لتكوين الفرق والانضمام للتحديات وتتبع إحصائيات الأداء وبناء مجتمع رياضي نشط.', technologies: ['Mobile', 'Firebase', 'REST API'], deliverables: ['تطبيق جوال', 'تغذية اجتماعية', 'لوحات النتائج'], link: '', images: ['/assets/images/projects/Sport-App/Screenshot 2024-04-25 at 3.02.12.png'], highlight: 'تحويل اللياقة البدنية إلى لعبة تنافسية', color: '#8258c8' },
  { id: 9, category: 'تطبيق إسلامي · ويب', title: 'قاعدة بيانات الأدعية', description: 'منصة ويب حديثة تعمل كقاعدة بيانات شاملة للأدعية الإسلامية مع بحث متقدم وتصنيف وتجربة مستخدم سلسة.', technologies: ['MySQL', 'Web Stack', 'Laravel'], deliverables: ['موقع ويب', 'محرك بحث', 'نظام إدارة المحتوى'], link: 'duaadatabase.com', images: ['/assets/images/projects/Duaa-App/Screenshot 2024-03-21 at 2.07.28.png'], highlight: 'آلاف الأدعية قابلة للبحث', color: '#2c84c8' },
  { id: 10, category: 'تقنية المطاعم · ذكي', title: 'DineOS — نظام المطعم الذكي', description: 'حل متكامل لإدارة المطاعم يشمل الطلب الذكي وعرض المطبخ وتحسين التكاليف بالذكاء الاصطناعي وتجربة العملاء.', technologies: ['Android', 'Web', 'REST API', 'POS', 'AI'], deliverables: ['تطبيق الطلب الذكي', 'عرض المطبخ', 'لوحة المدير', 'تحليلات الذكاء الاصطناعي'], link: 'pos.wiz-tech.co', images: ['/assets/images/projects/DineOS/Screenshot 2024-04-25 at 2.03.29.png', '/assets/images/projects/DineOS/Screenshot 2024-04-25 at 2.03.32.png'], highlight: 'الذكاء الاصطناعي يخفض التكاليف التشغيلية بنسبة 30%', color: '#45d0bd' },
  { id: 11, category: 'ذكاء اصطناعي · روبوتات · صحة', title: 'روبوت علاج التوحد', description: 'روبوت علاجي ذكي لعلاج اضطراب طيف التوحد — برامج تكيفية وتفاعلات منظمة وتتبع آمن للبيانات.', technologies: ['Embedded Systems', 'AI', 'Database'], deliverables: ['نظام الروبوت العلاجي', 'تكامل الخلفية', 'لوحة البيانات'], link: 'qlickhealth.ai', images: [], highlight: 'ريادة علاج التوحد بمساعدة الذكاء الاصطناعي', color: '#8258c8' },
  { id: 12, category: 'SaaS · منصة مساجد', title: 'منصة المساجد', description: 'منظومة رقمية متكاملة لإدارة المساجد — لوحة ويب ومحتوى وأوقات صلاة وفعاليات وتبرعات وأدوات مجتمعية.', technologies: ['Laravel', 'Vue.js', 'PHP', 'Android', 'iOS'], deliverables: ['لوحة المشرف', 'REST API', 'خلفية الجوال'], link: 'https://masjid.hopetechapps.com', images: [], highlight: 'منصة مركزية لإدارة المساجد', color: '#2c84c8' },
  { id: 13, category: 'ذكاء اصطناعي · تعليم · إسلامي', title: 'عُمان ترتيل — قرآن بالذكاء الاصطناعي', description: 'مساعد حفظ القرآن المدعوم بالذكاء الاصطناعي الذي يستمع للتلاوة الفورية ويكتشف الأخطاء ويعطي تغذية راجعة تصحيحية فورية.', technologies: ['AI', 'Speech Recognition', 'Cloud', 'Mobile'], deliverables: ['تطبيق جوال', 'محرك الذكاء الاصطناعي', 'متتبع التقدم'], link: '', images: ['/assets/images/projects/Islamic-App/Screenshot 2024-04-25 at 0.25.29.png'], highlight: 'تصحيح أخطاء القرآن في الوقت الفعلي', color: '#45d0bd' },
  { id: 14, category: 'ذكاء اصطناعي · IoT · مرافق', title: 'جامع قراءات العدادات', description: 'نظام ذكي لجمع قراءات عدادات الكهرباء آليًا باستخدام الذكاء الاصطناعي وOCR — تكامل فوري وتحليلات فواتير ولوحة شاملة.', technologies: ['Flutter', 'AI', 'OCR', 'REST API', 'Real-time'], deliverables: ['تطبيق جوال', 'لوحة ويب', 'تحليل القراءة بالذكاء الاصطناعي'], link: 'https://collector.wiz-tech.co', images: [], highlight: 'قراءة العدادات الآلية على نطاق واسع', color: '#8258c8' },
  { id: 15, category: 'SDK · رؤية حاسوبية', title: 'MRZ Scanner Android SDK', description: 'حزمة تطوير Android عالية الدقة لقراءة والتحقق من بيانات MRZ في جوازات السفر باستخدام OCR ومعالجة الصور — مبنية للمطارات والفنادق والتقنية المالية.', technologies: ['Android', 'Kotlin', 'Tesseract OCR', 'OpenCV'], deliverables: ['Android SDK', 'محلل MRZ', 'محرك التحقق'], link: '', images: [], highlight: 'مسح جوازات سفر بدرجة المطارات', color: '#2c84c8' },
  { id: 16, category: 'إدارة فعاليات · حجز', title: 'نظام FIFA عرب قطر', description: 'نظام إدارة فعاليات وحجز متخصص للإقامة وتذاكر VIP خلال الفعاليات الكبرى مثل كأس العرب FIFA.', technologies: ['Web', 'Mobile', 'Booking Systems'], deliverables: ['نظام الحجز', 'لوحة VIP', 'إدارة الفعاليات'], link: 'seating.wiz-tech.co', images: [], highlight: 'مستخدم في فعاليات كأس العرب FIFA', color: '#45d0bd' },
  { id: 17, category: 'تقنية مالية · جوال', title: 'تطبيق Safqa', description: 'تطبيق ذكي لإدارة الفواتير والحسابات — إنشاء وإرسال وتتبع الفواتير مع تتبع الدفع الكامل وحفظ السجلات المالية.', technologies: ['Flutter', 'Laravel', 'Firebase', 'REST API'], deliverables: ['تطبيق جوال (Android وiOS)', 'نظام الفواتير', 'لوحة الحسابات'], link: 'https://safqa.wiz-tech.co', images: [], highlight: 'تبسيط الفوترة للأعمال', color: '#8258c8' },
  { id: 18, category: 'تقنية مالية · ذكاء اصطناعي', title: 'Smart Ledger', description: 'نظام إدارة مالية بالذكاء الاصطناعي لتحليل المعاملات وإدارة الحسابات وتوليد رؤى مالية ذكية.', technologies: ['AI', 'Web', 'Financial APIs'], deliverables: ['لوحة المالية', 'تحليلات الذكاء الاصطناعي', 'إدارة الحسابات'], link: 'demo.wiz-tech.co', images: [], highlight: 'ذكاء مالي مدعوم بالذكاء الاصطناعي', color: '#2c84c8' },
  { id: 19, category: 'منصة سفر · ويب', title: 'BlueYamama.com', description: 'منصة تجميع سفر تقارن العروض من محركات حجز متعددة — ابحث عن أفضل الرحلات والفنادق والرحلات في مكان واحد.', technologies: ['Web', 'Aggregation API', 'Search'], deliverables: ['موقع التجميع', 'محرك المقارنة', 'نظام البحث'], link: 'BlueYamama.com', images: [], highlight: 'أفضل عروض السفر في مكان واحد', color: '#45d0bd' },
  { id: 20, category: 'ذكاء اصطناعي · سلامة · صناعي', title: 'تطبيق سلامة الحفارات', description: 'نظام سلامة بالذكاء الاصطناعي يكتشف وجود الإنسان بالقرب من الآلات الثقيلة وينبّه المشغلين فورًا — مبني لبيئات صناعية قاسية.', technologies: ['AI', 'Android', 'Edge AI', 'Computer Vision'], deliverables: ['تطبيق السلامة', 'نموذج الكشف', 'نظام التنبيه'], link: '', images: [], highlight: 'منع حوادث مواقع البناء', color: '#8258c8' },
  { id: 21, category: 'تجارة إلكترونية · أثاث', title: 'BM Meubelen', description: 'موقع تجارة إلكترونية أنيق للأثاث الخشبي المصنوع يدويًا — تصفح الفئات وقائمة الرغبات وسلة التسوق ونظام طلب التصميم المخصص.', technologies: ['Web', 'E-commerce', 'Responsive Design'], deliverables: ['موقع تجارة إلكترونية كامل', 'كتالوج المنتجات', 'نظام الطلب المخصص'], link: 'https://bmmeubelen.com', images: [], highlight: 'أثاث مصنوع يدويًا بجودة عالية', color: '#2c84c8' },
  { id: 22, category: 'معمار · ويب', title: 'Utopia4Design', description: 'الموقع الرسمي لشركة يوتوبيا للهندسة — استشارات في الهندسة المعمارية والتخطيط العمراني والتصميم الداخلي في غزة.', technologies: ['Web', 'Responsive Framework', 'Custom Design'], deliverables: ['موقع الشركة', 'محفظة المشاريع', 'نظام التواصل'], link: 'https://utopia4design.com', images: [], highlight: 'مكتب معمار راسخ منذ 2004', color: '#45d0bd' },
  { id: 23, category: 'ثقافي · نشر', title: 'مركز البحث الشرقي', description: 'منصة ثقافية وعلمية شاملة لمركز بحث عربي — مكتبة إلكترونية ومتجر كتب وتدوين واستطلاعات رأي وأنشطة تعليمية.', technologies: ['Web', 'CMS', 'E-commerce', 'Multi-language'], deliverables: ['موقع كامل', 'مكتبة إلكترونية', 'متجر كتب', 'نظام تدوين'], link: 'https://east-cr.com', images: [], highlight: 'مرجع النشر الثقافي العربي', color: '#8258c8' },
];

const CATEGORIES = ['All', 'HealthTech', 'Mobile', 'AI', 'Web', 'Restaurant Tech', 'Islamic App', 'FinTech', 'E-commerce'];
const CATEGORIES_AR = ['الكل', 'تقنية الصحة', 'جوال', 'ذكاء اصطناعي', 'ويب', 'تقنية المطاعم', 'تطبيقات إسلامية', 'تقنية مالية', 'تجارة إلكترونية'];

const glass = {
  background: 'rgba(10, 10, 18, 0.58)',
  backdropFilter: 'blur(18px)',
  WebkitBackdropFilter: 'blur(18px)',
  border: '1px solid rgba(255,255,255,0.07)',
  borderRadius: '20px',
};

const Projects = () => {
  const { i18n } = useTranslation();
  const isAr = i18n.language === 'ar';
  const getPath = useLangPath();
  const [activeFilter, setActiveFilter] = useState('All');
  const [selected, setSelected] = useState(null);

  const filteredIds = activeFilter === 'All'
    ? ALL_PROJECTS.map(p => p.id)
    : ALL_PROJECTS.filter(p => p.category.toLowerCase().includes(activeFilter.toLowerCase())).map(p => p.id);

  const allProjects = isAr ? ALL_PROJECTS_AR : ALL_PROJECTS;
  const filtered = allProjects.filter(p => filteredIds.includes(p.id));

  return (
    <>
      {/* ── Hero ── */}
      <section style={{ padding: '170px 0 50px', textAlign: 'center', background: 'transparent' }}>
        <div className="container">
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: '8px',
            background: 'rgba(130,88,200,0.12)', border: '1px solid rgba(130,88,200,0.3)',
            borderRadius: '50px', padding: '6px 18px', marginBottom: '24px',
          }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#8258c8', display: 'inline-block' }} />
            <span style={{ color: '#8258c8', fontSize: '13px', fontWeight: 600 }}>{isAr ? '23 مشروعاً تم تسليمه' : '23 Delivered Projects'}</span>
          </div>
          <h1 style={{ color: '#fff', fontSize: 'clamp(2.2rem, 5vw, 3.6rem)', fontWeight: 700, lineHeight: 1.2, marginBottom: 18 }}>
            {isAr ? <>نماذج من <span style={{ background: 'linear-gradient(102deg, #8258c8, #2c84c8)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>أعمال مُسلَّمة</span></> : <>Sample of <span style={{ background: 'linear-gradient(102deg, #8258c8, #2c84c8)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Shipped Work</span></>}
          </h1>
          <p style={{ color: '#6b6b88', maxWidth: '580px', margin: '0 auto', fontSize: '1.05rem', lineHeight: 1.7 }}>
            {isAr ? 'منتجات حقيقية لعملاء حقيقيين في تقنية الصحة والذكاء الاصطناعي وتقنية المطاعم والتطبيقات الإسلامية والتقنية المالية وغيرها — أكثر من 8 دول و10 صناعات.' : 'Real products built for real clients across HealthTech, AI, Restaurant Tech, Islamic Apps, FinTech, and more — 8+ countries, 10+ industries.'}
          </p>
        </div>
      </section>

      {/* ── Stats Bar ── */}
      <section style={{ background: 'transparent', padding: '0 0 50px' }}>
        <div className="container">
          <div className="row g-3 justify-content-center">
            {(isAr ? [
              { value: '10+', label: 'صناعة مخدومة' },
              { value: '8+', label: 'دولة وصلنا إليها' },
              { value: '100%', label: 'رضا العملاء' },
            ] : [
              { value: '10+', label: 'Industries Served' },
              { value: '8+', label: 'Countries Reached' },
              { value: '100%', label: 'Client Satisfaction' },
            ]).map(s => (
              <div className="col-6 col-md-3" key={s.label}>
                <div style={{ ...glass, padding: '22px', textAlign: 'center' }}>
                  <div style={{ fontSize: '2rem', fontWeight: 800, background: 'linear-gradient(102deg, #8258c8, #2c84c8)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                    {s.value}
                  </div>
                  <div style={{ color: '#6b6b88', fontSize: '0.85rem', marginTop: 4 }}>{s.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Category Filter ── */}
      <section style={{ background: 'transparent', padding: '0 0 40px' }}>
        <div className="container">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', justifyContent: 'center' }}>
            {CATEGORIES.map((cat, idx) => (
              <button
                key={cat}
                onClick={() => setActiveFilter(cat)}
                style={{
                  padding: '8px 22px', borderRadius: '50px', border: 'none', cursor: 'pointer',
                  fontSize: '13px', fontWeight: 600, transition: 'all 0.25s',
                  background: activeFilter === cat
                    ? 'linear-gradient(102deg, #8258c8, #2c84c8)'
                    : 'rgba(255,255,255,0.06)',
                  color: activeFilter === cat ? '#fff' : 'rgba(255,255,255,0.55)',
                  border: activeFilter === cat ? 'none' : '1px solid rgba(255,255,255,0.1)',
                }}
              >
                {isAr ? CATEGORIES_AR[idx] : cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ── Projects Grid ── */}
      <section style={{ background: 'transparent', padding: '0 0 100px' }}>
        <div className="container">
          <div className="row g-4">
            {filtered.map(project => (
              <div className="col-lg-4 col-md-6" key={project.id}>
                <div
                  className="tilt-card"
                  onClick={() => setSelected(project)}
                  style={{
                    ...glass, overflow: 'hidden', cursor: 'pointer', height: '100%',
                    display: 'flex', flexDirection: 'column',
                    transition: 'transform 0.35s ease, box-shadow 0.35s ease',
                  }}
                >
                  {/* Image / placeholder */}
                  <div style={{
                    height: 200, overflow: 'hidden',
                    background: project.images.length ? '#0a0a14' : `linear-gradient(135deg, ${project.color}18 0%, #0a0a14 100%)`,
                    position: 'relative',
                  }}>
                    {project.images.length > 0 && (
                      <img
                        src={project.images[0]}
                        alt={project.title}
                        style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top', display: 'block' }}
                        loading="lazy"
                      />
                    )}
                    {!project.images.length && (
                      <div style={{
                        position: 'absolute', inset: 0, display: 'flex',
                        alignItems: 'center', justifyContent: 'center',
                      }}>
                        <div style={{
                          width: 60, height: 60, borderRadius: '50%',
                          background: `${project.color}22`,
                          border: `2px solid ${project.color}44`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={project.color} strokeWidth="1.5">
                            <rect x="2" y="3" width="20" height="14" rx="2" />
                            <path d="M8 21h8M12 17v4" />
                          </svg>
                        </div>
                      </div>
                    )}
                    {/* category badge */}
                    <span style={{
                      position: 'absolute', top: 12, left: 12,
                      background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(8px)',
                      border: `1px solid ${project.color}44`,
                      color: project.color, fontSize: '11px', fontWeight: 700,
                      padding: '4px 12px', borderRadius: '50px',
                    }}>
                      {project.category.split(' · ')[0]}
                    </span>
                  </div>

                  {/* Content */}
                  <div style={{ padding: '22px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <h3 style={{ color: '#fff', fontSize: '1.05rem', fontWeight: 700, marginBottom: 10 }}>
                      {project.title}
                    </h3>
                    <p style={{ color: '#6b6b88', fontSize: '0.875rem', lineHeight: 1.6, marginBottom: 16, flex: 1 }}>
                      {project.description.length > 110 ? project.description.slice(0, 110) + '…' : project.description}
                    </p>

                    {/* Tech tags */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: 16 }}>
                      {project.technologies.slice(0, 3).map(t => (
                        <span key={t} style={{
                          fontSize: '10px', fontWeight: 600, padding: '3px 9px', borderRadius: '20px',
                          background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
                          color: 'rgba(255,255,255,0.55)',
                        }}>{t}</span>
                      ))}
                    </div>

                    {/* Highlight */}
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      padding: '8px 12px', borderRadius: '10px',
                      background: `${project.color}10`, border: `1px solid ${project.color}25`,
                    }}>
                      <span style={{ width: 6, height: 6, borderRadius: '50%', background: project.color, flexShrink: 0 }} />
                      <span style={{ color: project.color, fontSize: '12px', fontWeight: 600 }}>{project.highlight}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Detail Modal ── */}
      {selected && (
        <div
          onClick={() => setSelected(null)}
          style={{
            position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
            background: 'rgba(0,0,0,0.88)', zIndex: 9999,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              ...glass, maxWidth: 800, width: '100%', maxHeight: '90vh',
              overflowY: 'auto', position: 'relative',
              boxShadow: '0 40px 100px rgba(0,0,0,0.7)',
            }}
          >
            <button onClick={() => setSelected(null)} style={{
              position: 'absolute', top: 16, right: 16, background: 'rgba(255,255,255,0.08)',
              border: '1px solid rgba(255,255,255,0.1)', color: '#fff', width: 36, height: 36,
              borderRadius: '50%', cursor: 'pointer', fontSize: 18, display: 'flex',
              alignItems: 'center', justifyContent: 'center', zIndex: 10,
            }}>✕</button>

            {selected.images.length > 0 ? (
              <div style={{ borderRadius: '20px 20px 0 0', overflow: 'hidden' }}>
                <a href={selected.images[0]} target="_blank" rel="noopener noreferrer" title={isAr ? 'فتح بالحجم الكامل' : 'Open full size'}>
                  <img src={selected.images[0]} alt={selected.title}
                    style={{ width: '100%', height: 300, objectFit: 'cover', objectPosition: 'top', display: 'block', cursor: 'zoom-in' }} />
                </a>
                {selected.images.length > 1 && (
                  <div style={{ display: 'flex', gap: 6, padding: '6px 6px 0', background: '#0a0a14' }}>
                    {selected.images.slice(1).map((img, idx) => (
                      <a key={idx} href={img} target="_blank" rel="noopener noreferrer" style={{ flexShrink: 0 }}>
                        <img src={img} alt={`${selected.title} ${idx + 2}`}
                          style={{ height: 72, width: 110, objectFit: 'cover', objectPosition: 'top', borderRadius: 8, cursor: 'zoom-in', display: 'block' }} />
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div style={{
                height: 140, borderRadius: '20px 20px 0 0',
                background: `linear-gradient(135deg, ${selected.color}20 0%, #0a0a14 100%)`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <div style={{ fontSize: '3rem', opacity: 0.3 }}>⬡</div>
              </div>
            )}

            <div style={{ padding: '28px 32px 32px' }}>
              <span style={{
                background: `${selected.color}18`, border: `1px solid ${selected.color}35`,
                color: selected.color, fontSize: '12px', fontWeight: 700,
                padding: '4px 14px', borderRadius: '50px',
              }}>
                {selected.category}
              </span>
              <h2 style={{ color: '#fff', margin: '14px 0 16px', fontSize: '1.8rem', fontWeight: 700 }}>
                {selected.title}
              </h2>
              <p style={{ color: '#8888aa', lineHeight: 1.7, marginBottom: 24 }}>
                {selected.description}
              </p>

              <div className="row g-3">
                <div className="col-md-6">
                  <div style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 14, padding: '16px 20px' }}>
                    <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, fontWeight: 700, letterSpacing: 1, marginBottom: 10, textTransform: 'uppercase' }}>
                      {isAr ? 'التقنيات' : 'Technologies'}
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {selected.technologies.map(t => (
                        <span key={t} style={{
                          fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 20,
                          background: `${selected.color}15`, border: `1px solid ${selected.color}30`,
                          color: selected.color,
                        }}>{t}</span>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 14, padding: '16px 20px' }}>
                    <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, fontWeight: 700, letterSpacing: 1, marginBottom: 10, textTransform: 'uppercase' }}>
                      {isAr ? 'المخرجات' : 'Deliverables'}
                    </div>
                    {selected.deliverables.map(d => (
                      <div key={d} style={{ color: '#ccccdd', fontSize: 13, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ width: 5, height: 5, borderRadius: '50%', background: selected.color, flexShrink: 0 }} />
                        {d}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {selected.link && (
                <a
                  href={selected.link.startsWith('http') ? selected.link : `https://${selected.link}`}
                  target="_blank" rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 8, marginTop: 24,
                    background: 'linear-gradient(102deg, #8258c8, #2c84c8)',
                    color: '#fff', padding: '11px 28px', borderRadius: 50,
                    textDecoration: 'none', fontWeight: 700, fontSize: 14,
                  }}
                >
                  {isAr ? 'عرض المشروع المباشر ↗' : 'View Live Project ↗'}
                </a>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── CTA ── */}
      <section style={{
        background: 'rgba(130,88,200,0.06)', backdropFilter: 'blur(20px)',
        padding: '80px 0', textAlign: 'center',
        borderTop: '1px solid rgba(130,88,200,0.15)',
      }}>
        <div className="container">
          <h2 style={{ color: '#fff', marginBottom: 16, fontSize: 'clamp(1.8rem,4vw,2.6rem)', fontWeight: 700 }}>
            {isAr ? 'مستعد لبناء منتجك التالي؟' : 'Ready to build your next product?'}
          </h2>
          <p style={{ color: '#6b6b88', marginBottom: 32, maxWidth: 480, margin: '0 auto 32px' }}>
            {isAr ? 'انضم إلى قائمة متنامية من الشركات التي تثق في WIZ لتسليم برامج عالية الجودة بسرعة.' : 'Join the growing list of businesses that trust WIZ to ship quality software, fast.'}
          </p>
          <Link to={getPath('/contact')} style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            background: 'linear-gradient(102deg, #8258c8, #2c84c8)',
            color: '#fff', padding: '14px 36px', borderRadius: 50,
            textDecoration: 'none', fontWeight: 700, fontSize: 15,
            boxShadow: '0 8px 28px rgba(130,88,200,0.35)',
          }}>
            {isAr ? 'ابدأ مشروعًا ←' : 'Start a Project →'}
          </Link>
        </div>
      </section>
    </>
  );
};

export default Projects;
